import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { authApi } from '@/api/auth';
import { apiClient } from '@/api/client';
import { cn } from '@/lib/utils';

const loginSchema = z.object({
  mobile: z
    .string()
    .min(9, 'رقم الجوال يجب أن يكون 9 أرقام على الأقل')
    .max(10, 'رقم الجوال يجب أن يكون 10 أرقام كحد أقصى')
    .regex(/^\d{9,10}$/, 'رقم الجوال يجب أن يحتوي على أرقام فقط'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('+249');

  useEffect(() => {
    apiClient.get('/tenants/meta')
      .then((res) => {
        if (res.data?.phonePrefix) {
          setPhonePrefix(res.data.phonePrefix);
        }
      })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const mobileValue = watch('mobile');

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await authApi.requestOtp({
        mobile: data.mobile,
        clientType: 'support',
      });

      sessionStorage.setItem('login_mobile', data.mobile);
      toast.success(t('auth.otp.codeSent', 'تم إرسال رمز التحقق'));
      navigate('/verify-otp');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('auth.login.loginError', 'حدث خطأ أثناء تسجيل الدخول'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="text-center mb-2">
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('auth.login.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('auth.login.subtitle')}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Phone Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('auth.login.phoneLabel')}
          </label>
          <div
            className={cn(
              "flex items-center rounded-md border transition-all overflow-hidden bg-background",
              isFocused ? "border-primary ring-1 ring-primary" : "border-input hover:border-border300",
              errors.mobile && "border-destructive ring-1 ring-destructive"
            )}
          >
            {/* Country Code */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted border-s border-input">
              <span className="text-muted-foreground font-medium text-sm">{phonePrefix}</span>
            </div>
            {/* Input */}
            <input
              type="tel"
              inputMode="numeric"
              placeholder={t('auth.login.phonePlaceholder')}
              maxLength={10}
              className="flex-1 px-3 py-2 h-[38px] text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              dir="ltr"
              onFocus={() => setIsFocused(true)}
              {...register('mobile', {
                onBlur: () => setIsFocused(false),
              })}
            />
            {/* Validation Icon */}
            {mobileValue && mobileValue.length >= 9 && !errors.mobile && (
              <div className="ps-2 pe-3">
                <ShieldCheck className="w-5 h-5 text-success" />
              </div>
            )}
          </div>
          {errors.mobile && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.mobile.message}
            </p>
          )}
        </div>

        {/* Login Button */}
        <Button
          type="submit"
          loading={isLoading}
          className="w-full justify-center"
        >
          {!isLoading && (
            <>
              {t('auth.login.loginButton')}
              <ArrowLeft className="w-4 h-4 me-1" />
            </>
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <span className="flex-1 border-t border-border" />
        <span className="text-xs text-muted-foreground uppercase font-medium">{t('common.or', 'أو')}</span>
        <span className="flex-1 border-t border-border" />
      </div>

      {/* Admin Login Link */}
      <div className="text-center">
        <Link to="/admin-login" className="text-sm text-muted-foreground hover:text-primary">
          {t('auth.login.adminLogin')}
        </Link>
      </div>

      {/* Help */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          {t('auth.login.helpText', 'هل تواجه مشكلة؟')}{' '}
          <button
            type="button"
            className="text-primary font-medium hover:underline underline-offset-2"
          >
            {t('auth.login.contactSupport', 'تواصل مع الدعم')}
          </button>
        </p>
      </div>
    </div>
  );
}
